import React, { useEffect } from 'react';
import "@/App.css";
import "./light-theme.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LightboxProvider } from './components/LightboxProvider';
import { MapsNavProvider } from './components/MapsNavProvider';
import { ThemeProvider } from './contexts/ThemeContext';
import { DepartmentProvider } from './contexts/DepartmentContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import PwaInstallPrompt from './components/PwaInstallPrompt';
import ForcePasswordChange from './components/ForcePasswordChange';
import UpdateBanner from './components/UpdateBanner';
import PullToRefresh from './components/PullToRefresh';
import ErrorBoundary from './components/ErrorBoundary';
import { installGlobalErrorSafetyNet } from './lib/installGlobalErrorSafetyNet';

// iter162 — install ONCE at module load (before any axios call fires).
// Coerces err.response.data.detail object/array shapes into a safe string and
// suppresses CRA's red dev overlay for unhandled axios 403s.
installGlobalErrorSafetyNet();

import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import AcceptInvite from './pages/AcceptInvite';
import ComingSoon from './pages/ComingSoon';
import PublicPay from './pages/PublicPay';

import EmployeeLayout from './components/layouts/EmployeeLayout';
import TimeClock from './pages/employee/TimeClock';import TimeOff from './pages/employee/TimeOff';
import MyBenefits from './pages/employee/MyBenefits';
import Inventory from './pages/employee/Inventory';
import InventoryScanner from './pages/employee/InventoryScanner';
import EmployeeSchedule from './pages/employee/EmployeeSchedule';
import ScheduleClockGate from './components/employee/ScheduleClockGate';
import TeamSchedule from './pages/employee/TeamSchedule';
import Billing from './pages/employee/Billing';
import Members from './pages/employee/Members';

import CustomerLayout from './components/layouts/CustomerLayout';
import CustomerDashboard from './pages/customer/Dashboard';
import Invoices from './pages/customer/Invoices';
import Spending from './pages/customer/Spending';
import Estimates from './pages/customer/Estimates';
import ServiceRequest from './pages/customer/ServiceRequest';
import Membership from './pages/customer/Membership';

import VipLayout from './components/layouts/VipLayout';
import VipDashboard from './pages/vip/VipDashboard';
import VipReferrals from './pages/vip/VipReferrals';

import AdminLayout from './components/layouts/AdminLayout';
import Dashboard from './pages/admin/Dashboard';
import Schedule from './pages/admin/Schedule';
import AdminInvoices from './pages/admin/AdminInvoices';import BillingDiagnostics from './pages/admin/BillingDiagnostics';
import BillingMissing from './pages/admin/BillingMissing';
import AdminFollowups from './pages/admin/AdminFollowups';
import AdminFieldNotes from './pages/admin/AdminFieldNotes';
import PromoLinks from './pages/admin/PromoLinks';
import PromoPage from './pages/public/PromoPage';
import PmResetPassword from './pages/public/PmResetPassword';
import AdminEstimates from './pages/admin/AdminEstimates';
import Customers from './pages/admin/Customers';
import Employees from './pages/admin/Employees';
import AdminInventory from './pages/admin/AdminInventory';
import AdminMemberships from './pages/admin/AdminMemberships';
import AdminTimeOff from './pages/admin/AdminTimeOff';
import AdminVips from './pages/admin/AdminVips';
import AdminAnnouncements from './pages/admin/AdminAnnouncements';
import AdminAuditLog from './pages/admin/AdminAuditLog';
import AdminVipSettings from './pages/admin/AdminVipSettings';
import AdminPermissions from './pages/admin/AdminPermissions';
import AdminTimesheets from './pages/admin/AdminTimesheets';
import AdminBonusRequests from './pages/admin/AdminBonusRequests';
import AdminMergeCustomers from './pages/admin/AdminMergeCustomers';
import Payroll from './pages/admin/Payroll';
import Vehicles from './pages/admin/Vehicles';
import OnCall from './pages/admin/OnCall';
import AdminApprovals from './pages/admin/AdminApprovals';
import JobApprovals from './pages/admin/JobApprovals';
import AssetTracking from './pages/admin/AssetTracking';
import FormBuilder from './pages/admin/FormBuilder';
import ServiceTypesSettings from './pages/admin/ServiceTypesSettings';
import AdminDepartments from './pages/admin/AdminDepartments';
import AdminDivisions from './pages/admin/AdminDivisions';
import AdminRoles from './pages/admin/AdminRoles';
import JobTemplatesSettings from './pages/admin/JobTemplatesSettings';
import ServiceCatalog from './pages/admin/ServiceCatalog';
import PricebookImport from './pages/admin/PricebookImport';
import PaymentIntegrityDiagnostic from './pages/admin/PaymentIntegrityDiagnostic';
import OpportunityEngine from './pages/admin/OpportunityEngine';
import { TechPerformance, OpportunityBoard, RevenuePipeline, MarketingBoard } from './pages/admin/Dashboards';
import CustomerEquipment from './pages/customer/CustomerEquipment';
import AdminChangeApprovals from './pages/admin/AdminChangeApprovals';
import AdminTrash from './pages/admin/AdminTrash';
import MyChanges from './pages/MyChanges';
import AdminReminders from './pages/admin/AdminReminders';
import AdminServiceRequests from './pages/admin/AdminServiceRequests';
import AdminSystem from './pages/admin/AdminSystem';
import AdminSettings from './pages/admin/AdminSettings';
import AdminSmsSettings from './pages/admin/AdminSmsSettings';
import CommunicationSettings from './pages/admin/CommunicationSettings';
import KnowledgeBase from './pages/admin/KnowledgeBase';
import FieldServiceCatalog from './pages/employee/FieldServiceCatalog';
import Reports from './pages/admin/Reports';
import UserManagement from './pages/admin/UserManagement';
import DataTools from './pages/admin/DataTools';
import AdminVipTerms from './pages/admin/AdminVipTerms';
import VipTerms from './pages/vip/VipTerms';
import InventoryDashboard from './pages/admin/InventoryDashboard';
import InventoryItems from './pages/admin/InventoryItems';
import InventoryLocations from './pages/admin/InventoryLocations';
import InventoryStock from './pages/admin/InventoryStock';
import InventoryWorkflow from './pages/admin/InventoryWorkflow';
import Profile from './pages/Profile';
import SmsOptIn from './pages/SmsOptIn';
import SmsConsent from './pages/SmsConsent';
import SmsCompliance from './pages/SmsCompliance';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import EmployeeCustomers from './pages/employee/EmployeeCustomers';

// iter75 — Workforce Hub
import WorkforceHub from './pages/admin/hubs/WorkforceHub';
import { WORKFORCE_LEGACY_REDIRECTS } from './hubs/workforceTabs';
// iter75 Phase 2 — Inventory Hub
import InventoryHub from './pages/admin/hubs/InventoryHub';
import { INVENTORY_LEGACY_REDIRECTS } from './hubs/inventoryTabs';
// iter75 Phase 3 — Property Management Hub
import PmHub from './pages/admin/hubs/PmHub';

// Phase 21A — Property Manager Portal
import PortalLayoutPM from './components/layouts/PortalLayoutPM';
import PmDashboard from './pages/pm/Dashboard';
import PmProperties from './pages/pm/Properties';
import PmPropertyDetail from './pages/pm/PropertyDetail';
import PmSubmitRequest from './pages/pm/SubmitRequest';
import PmWorkOrders from './pages/pm/WorkOrders';
import PmWorkOrderDetail from './pages/pm/WorkOrderDetail';
import PmInvoices from './pages/pm/Invoices';
import PmPropertyHistory from './pages/pm/PropertyHistory';
import PmProfile from './pages/pm/Profile';
import AdminPmCompanies from './pages/admin/PmCompanies';
import AdminPmProperties from './pages/admin/PmProperties';
import AdminPmAccounts from './pages/admin/PmAccounts';
import AdminPmServiceRequests from './pages/admin/PmServiceRequests';
import AdminPoReports from './pages/admin/PoReports';
import AdminPmAssets from './pages/admin/PmAssets';
import PoApprovePublic from './pages/PoApprovePublic';
import AdminPmCrm from './pages/admin/PmCrm';
import AdminPmPropertyRequests from './pages/admin/PmPropertyRequests';
import PmPropertyRequestsPage from './pages/pm/PropertyRequests';
import PmContacts from './pages/pm/Contacts';
import PmNotifications from './pages/pm/Notifications';
import PmUsers from './pages/pm/PmUsers';
import PmAccessGroups from './pages/pm/PmAccessGroups';
import PmMyProperties from './pages/pm/PmMyProperties';
import PmBulkImport from './pages/pm/BulkImport';

const HomePage = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0A0A0A]">
        <div className="text-[#D4AF37] text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const techRoles = ['employee', 'technician'];
  const adminRoles = ['admin', 'super_admin', 'manager', 'service_manager', 'construction_manager', 'office_staff'];
  const pmPortalRoles = ['property_manager', 'asset_manager', 'maintenance_supervisor', 'pm_maintenance'];
  if (adminRoles.includes(user.role)) return <Navigate to="/admin/dashboard" replace />;
  if (techRoles.includes(user.role)) return <Navigate to="/employee/timeclock" replace />;
  if (user.role === 'customer') return <Navigate to="/customer/dashboard" replace />;
  if (user.role === 'vip') return <Navigate to="/vip/dashboard" replace />;
  if (pmPortalRoles.includes(user.role)) return <Navigate to="/pm/dashboard" replace />;

  return <Navigate to="/login" replace />;
};

function App() {
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js')
          .then((reg) => {
            // One-time recovery: nuke any stale caches from the v1 SW that cached
            // index.html and broke logins after a redeploy. Tracked via localStorage
            // so we only run it once per device.
            try {
              if (!localStorage.getItem('nexflow_sw_v355_flushed')) {
                if ('caches' in window) {
                  caches.keys().then((keys) =>
                    Promise.all(keys.filter((k) => k !== 'nexflow-v355').map((k) => caches.delete(k)))
                  ).catch(() => {});
                }
                navigator.serviceWorker.getRegistrations().then((regs) => {
                  regs.forEach((r) => {
                    if (r.active && !r.active.scriptURL.includes('service-worker.js')) {
                      r.unregister();
                    }
                  });
                }).catch(() => {});
                localStorage.setItem('nexflow_sw_v355_flushed', '1');
              }
            } catch { /* localStorage unavailable */ }
            // Always check for updates on load so deploys propagate fast.
            reg.update().catch(() => {});
          })
          .catch(() => { /* ignore */ });
      });
    }
  }, []);

  return (
    <AuthProvider>
      <DepartmentProvider>
        <BrowserRouter>
        <ThemeProvider>
        <LightboxProvider>
        <MapsNavProvider>
        <ErrorBoundary scope="App">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify" element={<VerifyEmail />} />
          <Route path="/accept-invite/:token" element={<AcceptInvite />} />
          <Route path="/pay/:token" element={<PublicPay />} />
          <Route path="/sms-opt-in" element={<SmsOptIn />} />
          <Route path="/sms-consent" element={<SmsConsent />} />
          <Route path="/po-approve/:token" element={<PoApprovePublic />} />
          <Route path="/pm/reset/:token" element={<PmResetPassword />} />
          <Route path="/public/sms-consent" element={<SmsConsent />} />
          <Route path="/sms-compliance" element={<SmsCompliance />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          {/* iter174 — Permanent public promo URLs. /offer is the short alias
              used by feather flags; /promo/:slug handles every other printed asset. */}
          <Route path="/offer" element={<PromoPage slug="offer" />} />
          <Route path="/promo/:slug" element={<PromoPage />} />

          <Route
            path="/employee"
            element={
              <ProtectedRoute allowedRoles={['employee', 'technician', 'admin', 'super_admin', 'manager', 'service_manager', 'construction_manager', 'office_staff']}>
                <EmployeeLayout />
              </ProtectedRoute>
            }
          >
            <Route path="timeclock" element={<TimeClock />} />
            <Route path="timesheet" element={<TimeClock />} />
            <Route path="customers" element={<EmployeeCustomers />} />
            <Route path="schedule" element={<ScheduleClockGate><EmployeeSchedule /></ScheduleClockGate>} />
            {/* iter120 — legacy /employee/team-schedule redirects to the new unified Field Schedule with team view. */}
            <Route path="team-schedule" element={<Navigate to="/employee/schedule?view=team" replace />} />
            <Route path="billing" element={<Billing />} />
            <Route path="members" element={<Members />} />
            <Route path="timeoff" element={<TimeOff />} />
            <Route path="benefits" element={<MyBenefits />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="inventory-scanner" element={<InventoryScanner />} />
            <Route path="profile" element={<Profile />} />
            <Route path="knowledge-base" element={<KnowledgeBase />} />
            <Route path="service-catalog" element={<FieldServiceCatalog />} />
            {/* iter233 — legacy field pricebook route → new Service Catalog */}
            <Route path="pricebook" element={<Navigate to="/employee/service-catalog" replace />} />
            <Route path="my-changes" element={<MyChanges />} />
          </Route>

          <Route
            path="/customer"
            element={
              <ProtectedRoute allowedRoles={['customer']}>
                <CustomerLayout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<CustomerDashboard />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="spending" element={<Spending />} />
            <Route path="estimates" element={<Estimates />} />
            <Route path="service-request" element={<ServiceRequest />} />
            <Route path="membership" element={<Membership />} />
            <Route path="equipment" element={<CustomerEquipment />} />
            <Route path="profile" element={<Profile />} />
          </Route>

          <Route
            path="/vip"
            element={
              <ProtectedRoute allowedRoles={['vip']}>
                <VipLayout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<VipDashboard />} />
            <Route path="referrals" element={<VipReferrals />} />
            <Route path="terms" element={<VipTerms />} />
            <Route path="profile" element={<Profile />} />
          </Route>

          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={['admin', 'super_admin', 'manager', 'service_manager', 'construction_manager', 'office_staff']}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="schedule" element={<Schedule />} />
            <Route path="invoices" element={<AdminInvoices />} />
            <Route path="billing-missing" element={<BillingMissing />} />
            <Route path="followups" element={<AdminFollowups />} />
            <Route path="field-notes" element={<AdminFieldNotes />} />
            <Route path="billing-diagnostics" element={<BillingDiagnostics />} />
            <Route path="promos" element={<PromoLinks />} />
            <Route path="estimates" element={<AdminEstimates />} />
            <Route path="customers" element={<Customers />} />
            {/* iter75 — Workforce Hub. Old routes redirect to the corresponding hub tab so
                bookmarks, screenshots, and Save-to-GitHub URLs keep working. */}
            <Route path="workforce" element={<WorkforceHub />} />
            <Route path="workforce/:tabKey" element={<WorkforceHub />} />
            {WORKFORCE_LEGACY_REDIRECTS.map(({ from, to }) => (
              <Route key={from} path={from.replace('/admin/', '')} element={<Navigate to={to} replace />} />
            ))}
            {/* iter75 Phase 2 — Inventory Hub */}
            <Route path="inventory-hub" element={<InventoryHub />} />
            <Route path="inventory-hub/:tabKey" element={<InventoryHub />} />
            {INVENTORY_LEGACY_REDIRECTS.map(({ from, to }) => (
              <Route key={from} path={from.replace('/admin/', '')} element={<Navigate to={to} replace />} />
            ))}
            <Route path="memberships" element={<AdminMemberships />} />
            <Route path="vips" element={<AdminVips />} />
            <Route path="vip-settings" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminVipSettings /></ProtectedRoute>} />
            <Route path="announcements" element={<AdminAnnouncements />} />
            {/* iter160 — Roles / Permissions is admin/super_admin ONLY. `manage_employees` must NOT grant access. */}
            <Route path="permissions" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminPermissions /></ProtectedRoute>} />
            <Route path="audit-log" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']} allowedPerms={['view_audit_log']}><AdminAuditLog /></ProtectedRoute>} />
            <Route path="merge-customers" element={<AdminMergeCustomers />} />
            <Route path="on-call" element={<OnCall />} />
            <Route path="assets" element={<AssetTracking />} />
            <Route path="form-builder" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><FormBuilder /></ProtectedRoute>} />
            <Route path="departments" element={<AdminDepartments />} />
            <Route path="divisions" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminDivisions /></ProtectedRoute>} />
            <Route path="roles" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminRoles /></ProtectedRoute>} />
            <Route path="service-types" element={<ServiceTypesSettings />} />
            <Route path="job-templates" element={<JobTemplatesSettings />} />
            <Route path="service-catalog" element={<ServiceCatalog />} />
            <Route path="pricebook-import" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><PricebookImport /></ProtectedRoute>} />
            <Route path="payment-diagnostic" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><PaymentIntegrityDiagnostic /></ProtectedRoute>} />
            <Route path="opportunities" element={<OpportunityEngine />} />
            <Route path="reports/tech-performance" element={<ProtectedRoute allowedRoles={['admin', 'super_admin', 'manager', 'service_manager', 'construction_manager']} allowedPerms={['view_financial']}><TechPerformance /></ProtectedRoute>} />
            <Route path="reports/opportunity-board" element={<ProtectedRoute allowedRoles={['admin', 'super_admin', 'manager', 'service_manager', 'construction_manager']}><OpportunityBoard /></ProtectedRoute>} />
            <Route path="reports/revenue-pipeline" element={<ProtectedRoute allowedRoles={['admin', 'super_admin', 'manager', 'service_manager', 'construction_manager']} allowedPerms={['view_financial']}><RevenuePipeline /></ProtectedRoute>} />
            <Route path="reports/marketing" element={<ProtectedRoute allowedRoles={['admin', 'super_admin', 'manager', 'service_manager', 'construction_manager']} allowedPerms={['view_financial']}><MarketingBoard /></ProtectedRoute>} />
            <Route path="change-approvals" element={<AdminChangeApprovals />} />
            <Route path="my-changes" element={<MyChanges />} />
            <Route path="trash" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminTrash /></ProtectedRoute>} />
            <Route path="reminders" element={<AdminReminders />} />
            <Route path="service-requests" element={<AdminServiceRequests />} />
            <Route path="system" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminSystem /></ProtectedRoute>} />
            <Route path="reports" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']} allowedPerms={['view_financial']}><Reports /></ProtectedRoute>} />
            <Route path="users" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']} allowedPerms={['invite_employees', 'onboard_employees']}><UserManagement /></ProtectedRoute>} />
            <Route path="data-tools" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><DataTools /></ProtectedRoute>} />
            <Route path="vip-terms" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminVipTerms /></ProtectedRoute>} />
            <Route path="pm" element={<PmHub />} />
            <Route path="pm/:tabKey" element={<PmHub />} />
            <Route path="settings" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminSettings /></ProtectedRoute>} />
            <Route path="sms" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']} allowedPerms={['company_settings']}><AdminSmsSettings /></ProtectedRoute>} />
            <Route path="communication" element={<CommunicationSettings />} />
            <Route path="profile" element={<Profile />} />
            <Route path="timeclock" element={<TimeClock />} />
          </Route>

          {/* Phase 21A — Property Manager Portal */}
          <Route
            path="/pm"
            element={
              <ProtectedRoute allowedRoles={['property_manager', 'asset_manager', 'maintenance_supervisor', 'pm_maintenance']}>
                <PortalLayoutPM />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<PmDashboard />} />
            <Route path="properties" element={<PmProperties />} />
            <Route path="properties/:id" element={<PmPropertyDetail />} />
            <Route path="submit" element={<PmSubmitRequest />} />
            <Route path="work-orders" element={<PmWorkOrders />} />
            <Route path="work-orders/:id" element={<PmWorkOrderDetail />} />
            <Route path="invoices" element={<PmInvoices />} />
            <Route path="property-history" element={<PmPropertyHistory />} />
            <Route path="property-requests" element={<PmPropertyRequestsPage />} />
            <Route path="contacts" element={<PmContacts />} />
            <Route path="notifications" element={<PmNotifications />} />
            <Route path="profile" element={<PmProfile />} />
            <Route path="users" element={<PmUsers />} />
            <Route path="access-groups" element={<PmAccessGroups />} />
            <Route path="my-properties" element={<PmMyProperties />} />
            <Route path="bulk-import" element={<PmBulkImport />} />
          </Route>
        </Routes>
        </ErrorBoundary>
        <Toaster position="top-right" theme="dark" />
        <PwaInstallPrompt />
        <ForcePasswordChange />
        <UpdateBanner />
        <PullToRefresh />
        </MapsNavProvider>
        </LightboxProvider>
        </ThemeProvider>
      </BrowserRouter>
      </DepartmentProvider>
    </AuthProvider>
  );
}

export default App;
